import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import {
  DeviceFilter,
  DeviceFilterAttribute,
  MetaDataAttributes,
} from 'models';
import { createFilters } from 'utils/filtering';

import Button from 'components/UI/Button';
import SelectField, { OptionType } from 'components/UI/SelectField';
import InputField from 'components/UI/InputField';

import { ButtonGrid, TwoColumnGrid } from 'styles';
import { FilterGroupContainer } from './styled';

type Props = {
  onFilter: (value: DeviceFilter[]) => void;
  onClear: () => void;
  data: MetaDataAttributes;
};

const DeviceFilters: FC<Props> = ({ onFilter, onClear, data }) => {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  // Watch
  const [
    pnc,
    serialNumber,
    updateStatus,
    lastConnectionAfter,
    lastConnectionBefore,
    happFirmwareVersion,
    testDevice,
  ] = watch([
    DeviceFilterAttribute.PNC,
    DeviceFilterAttribute.SerialNumber,
    DeviceFilterAttribute.UpdateStatus,
    DeviceFilterAttribute.LastConnectionAfter,
    DeviceFilterAttribute.LastConnectionBefore,
    DeviceFilterAttribute.HappFirmwareVersion,
    DeviceFilterAttribute.TestDevice,
  ]);
  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      const filters = createFilters<DeviceFilter>(values);
      if (filters.length) {
        onFilter(filters);
      }
    },
    [onFilter],
  );

  // Reset
  const onReset = useCallback(() => {
    reset({
      iprId: '',
      pnc: '',
      serialNumber: '',
      lastConnectionBefore: '',
      lastConnectionAfter: '',
      happFirmwareVersion: '',
      testDevice: false,
    });
    onClear();
  }, [reset, onClear]);

  // Set date
  const setDate = useCallback(
    (name: DeviceFilterAttribute) => (date: Date) => {
      setValue(name, format(date, 'yyyy-MM-dd'), {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  // Select option
  const onSelect = useCallback(
    (name: DeviceFilterAttribute) => (value: string) => setValue(name, value),
    [setValue],
  );

  // Parse label
  const parseOptionLabel = (langId: string, listName: string): string => {
    const name = listName.split(' ').join('_').toLowerCase();
    const id = `${langId}_${name}`;
    return `${intl.formatMessage({ id })}`;
  };

  // Status
  const statusOptions: OptionType[] = data.updateStatuses.map(({ name }) => ({
    key: name,
    label: parseOptionLabel('connectivity.update_status', name),
    value: name,
  }));

  const testDeviceOptions: OptionType[] = [
    {
      key: 'yes',
      label: 'yes',
      value: 'true',
    },
    {
      key: 'no',
      label: 'no',
      value: 'false',
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TwoColumnGrid>
        <InputField
          description="connectivity.serialNo"
          register={register('serialNumber')}
          defaultValue={serialNumber}
        />
        <SelectField
          name={DeviceFilterAttribute.TestDevice}
          register={register(DeviceFilterAttribute.TestDevice)}
          onSelect={onSelect(DeviceFilterAttribute.TestDevice)}
          placeholder={intl.formatMessage({ id: 'select.placeholder' })}
          label={intl.formatMessage({ id: 'connectivity.onlyTestDevices' })}
          defaultValue={testDevice}
          options={testDeviceOptions}
        />
      </TwoColumnGrid>
      <FilterGroupContainer>
        <Heading type={HeadingType.h4} uppercase>
          <FormattedMessage id="connectivity.shadowFilter" />
        </Heading>
        <TwoColumnGrid>
          <InputField
            description="connectivity.articleNo"
            register={register('pnc')}
            defaultValue={pnc}
          />
          <div/>
          <InputField
            type="date"
            description="connectivity.lastConnection_after"
            defaultValue={lastConnectionAfter}
            register={register(DeviceFilterAttribute.LastConnectionAfter)}
            setDate={setDate(DeviceFilterAttribute.LastConnectionAfter)}
            error={errors.lastConnectionAfter}
          />
          <InputField
            type="date"
            description="connectivity.lastConnection_before"
            register={register(DeviceFilterAttribute.LastConnectionBefore)}
            setDate={setDate(DeviceFilterAttribute.LastConnectionBefore)}
            defaultValue={lastConnectionBefore}
            error={errors.lastConnectionBefore}
          />
        </TwoColumnGrid>
      </FilterGroupContainer>
      <FilterGroupContainer>
        <Heading type={HeadingType.h4} uppercase>
          <FormattedMessage id="connectivity.happ" />
        </Heading>
        <TwoColumnGrid>
          <InputField
            description="connectivity.firmwareVersion"
            register={register('happFirmwareVersion')}
            defaultValue={happFirmwareVersion}
          />
          <SelectField
            name={DeviceFilterAttribute.UpdateStatus}
            register={register(DeviceFilterAttribute.UpdateStatus)}
            onSelect={onSelect(DeviceFilterAttribute.UpdateStatus)}
            placeholder={intl.formatMessage({ id: 'select.placeholder' })}
            label={intl.formatMessage({ id: 'connectivity.softwareUpdate' })}
            defaultValue={updateStatus}
            options={statusOptions}
          />
        </TwoColumnGrid>
      </FilterGroupContainer>
      <ButtonGrid>
        <Button
          color="primary"
          backgroundColor="transparent"
          onClick={onReset}
          marginRight
        >
          <FormattedMessage id="filters.btn_clear" />
        </Button>
        <Button submit id="filter_devices_btn">
          <FormattedMessage id="filters.btn_submit" />
        </Button>
      </ButtonGrid>
    </form>
  );
};

export default DeviceFilters;
